import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type CompanyDetails = {
  id: string;
  name: string;
  owner_id: string | null;
  status: 'active' | 'suspended';
  org_number: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  created_at: string;
};

export function useCompanySettings(companyId: string | null | undefined) {
  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!companyId) {
      setCompany(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase
      .from('companies')
      .select('*')
      .eq('id', companyId)
      .single();

    if (err) {
      setError(err.message);
      setCompany(null);
    } else {
      setCompany(data as CompanyDetails);
      setError(null);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  async function updateCompany(patch: Partial<CompanyDetails>) {
    if (!companyId) return { error: 'No company linked' };
    const { error: err } = await supabase
      .from('companies')
      .update(patch)
      .eq('id', companyId);
    if (!err) await refetch();
    return { error: err?.message ?? null };
  }

  return { company, loading, error, refetch, updateCompany };
}
